<div class="tags-with-ai-container">
  <!-- Input Section -->
  <div class="input-container">
    <ng-select
      #tagSelect
      [addTag]="false"
      [attr.disabled]="isReadOnly ? true : null"
      [bindLabel]="'name'"
      [clearable]="false"
      [clearSearchOnAdd]="true"
      [closeOnSelect]="false"
      [dropdownPosition]="dropdownPosition"
      [formControl]="formControl"
      [groupBy]="'group'"
      [hideSelected]="true"
      [items]="tagOptions$ | async"
      [loading]="false"
      [multiple]="true"
      [placeholder]="props.placeholder || ''"
      [required]="props.required"
      [typeahead]="input$"
      [virtualScroll]="true"
      (change)="onTagsChange()"
      (scrollToEnd)="onSelectScrollToEnd()"
      class="tags-with-ai-select"
      appendTo="body"
    >
      <ng-template let-item="item" ng-optgroup-tmp>
        <div *ngIf="item.group" class="text-muted fw-bold small">{{ item.group }}</div>
      </ng-template>
      <ng-template let-item="item" let-search="searchTerm" ng-option-tmp>
        <div>
          <span [innerHTML]="item.name | highlight: search"></span>
          <small class="text-muted ms-1">({{ item.topicCount || item.topic_count || 0 }})</small>
          <div *ngIf="getTagDescription(item)" class="tag-description small text-muted mt-1">
            <span [innerHTML]="getTagDescription(item) | highlight: search"></span>
          </div>
        </div>
      </ng-template>

      <ng-template ng-header-tmp>
        <!-- Browse Mode Selector -->
        <div class="tag-browse-mode-selector-dropdown d-flex justify-content-between align-items-center">
          <div class="btn-group btn-group-sm" role="group">
            <button
              [class.btn-dark]="browseMode !== 'alphabetical'"
              [class.btn-primary]="browseMode === 'alphabetical'"
              (mousedown)="onModeChange($event, 'alphabetical')"
              class="btn"
              type="button"
            >
              <fa-icon [icon]="['fas', 'sort-alpha-down']" class="me-1"></fa-icon>
              {{ "A-Z" | translate }}
            </button>
            <button
              [class.btn-dark]="browseMode !== 'category'"
              [class.btn-primary]="browseMode === 'category'"
              (mousedown)="onModeChange($event, 'category')"
              class="btn"
              type="button"
            >
              <fa-icon [icon]="['fas', 'layer-group']" class="me-1"></fa-icon>
              {{ "Categories" | translate }}
            </button>
            <button
              [class.btn-dark]="browseMode !== 'popularity'"
              [class.btn-primary]="browseMode === 'popularity'"
              (mousedown)="onModeChange($event, 'popularity')"
              class="btn"
              type="button"
            >
              <fa-icon [icon]="['fas', 'fire']" class="me-1"></fa-icon>
              {{ "Popular" | translate }}
            </button>
          </div>
          <button
            [ngbTooltip]="'Close' | translate"
            (mousedown)="onCloseDropdown($event)"
            class="btn btn-dark btn-sm btn-no-block ms-2"
            type="button"
          >
            <fa-icon [icon]="['fas', 'times']" class="me-0"></fa-icon>
          </button>
        </div>
      </ng-template>

      <ng-template ng-loadingtext-tmp>
        <div class="d-flex justify-content-center py-2">
          <astrobin-loading-indicator></astrobin-loading-indicator>
        </div>
      </ng-template>

      <ng-template ng-notfound-tmp>
        <div *ngIf="!isLoadingTags" class="text-center py-2 text-muted">
          {{ "No tags found" | translate }}
        </div>
        <div *ngIf="isLoadingTags" class="d-flex justify-content-center py-2">
          <astrobin-loading-indicator></astrobin-loading-indicator>
        </div>
      </ng-template>

      <ng-template ng-footer-tmp>
        <div class="suggest-new-tag-footer">
          <button (mousedown)="onSuggestNewTag($event)" class="btn btn-link btn-sm suggest-new-tag-btn" type="button">
            <fa-icon [icon]="['fas', 'plus-circle']" class="me-1"></fa-icon>
            {{ "Can't find what you're looking for? Suggest a new tag!" | translate }}
          </button>
        </div>
      </ng-template>
    </ng-select>

    <div class="input-icons">
      <!-- Loading spinner when generating -->
      <fa-icon
        *ngIf="isTagsGenerating$ | async"
        [icon]="['fas', 'spinner']"
        [ngbTooltip]="'Generating tag suggestions...' | translate"
        class="icon-generating"
        animation="spin"
      >
      </fa-icon>

      <!-- AI robot icon for generating tags -->
      <button
        #tooltip="ngbTooltip"
        *ngIf="(isTagsGenerating$ | async) !== true && props.bodyContent$"
        [class.disabled]="!canGenerateTags || isReadOnly"
        [ngbTooltip]="
          canGenerateTags
            ? ('Generate tag suggestions' | translate)
            : ('Add more content before generating tags' | translate)
        "
        [openDelay]="500"
        (click)="onGenerateTagsClick(tooltip)"
        class="btn btn-link ai-generate-btn"
        type="button"
      >
        <astrobin-sparkles-icon class="icon-ai me-0"></astrobin-sparkles-icon>
      </button>
    </div>
  </div>

  <!-- Tag Suggestions Section -->
  <div *ngIf="hasPendingSuggestions && pendingSuggestions.length > 0" class="suggestions-container">
    <div class="suggestions-list">
      <div *ngFor="let tag of pendingSuggestions" class="suggestion-pill">
        <astrobin-sparkles-icon [height]="14" [width]="14"></astrobin-sparkles-icon>
        <span class="suggestion-text">{{ tag.name }}</span>
        <span
          *ngIf="tag.confidence !== undefined"
          [attr.data-confidence]="getConfidenceLevel(tag.confidence)"
          [ngbTooltip]="'Confidence level' | translate"
          class="confidence-indicator"
        >
          {{ (tag.confidence * 100).toFixed(0) }}%
        </span>
        <div class="suggestion-actions">
          <button
            [ngbTooltip]="'Accept suggestion' | translate"
            (click)="acceptSuggestion(tag)"
            class="btn btn-link btn-sm accept-btn"
            type="button"
          >
            <fa-icon [icon]="['fas', 'check']"></fa-icon>
          </button>
          <button
            [ngbTooltip]="'Reject suggestion' | translate"
            (click)="rejectSuggestion(tag)"
            class="btn btn-link btn-sm reject-btn"
            type="button"
          >
            <fa-icon [icon]="['fas', 'times']"></fa-icon>
          </button>
        </div>
      </div>
    </div>
  </div>
</div>
